// src/views/menage/BarreActionsBloc.jsx — LE MENU DU PREMIER CLIC, partie
// basse (maquette validée Dimitri 2026-08-19).
//
// La rangée d'agents NE BOUGE PAS (geste principal) ; cette barre REMPLACE
// tout ce qui vivait dessous : l'ancien bouton « Détails du ménage » et les
// entrées/mentions contrôle. QUATRE BOUTONS ÉGAUX — icône 19 px + MOT dessous
// (obligatoire) — sur fond #f6f8fa, séparés de la rangée par un trait :
//   1. « Code »      → une fenêtre plein écran avec TOUS les codes renseignés,
//                      en gros, chacun avec son « Copier » ;
//   2. « Contrôler » → un CHOIX (je contrôle maintenant / assigner à…), la
//                      qualité du MÉNAGE — aucun lien avec les incidents ;
//   3. « Signaler »  → l'agent assigné signale un incident ; tout autre
//                      utilisateur LIT les incidents du ménage (« Incidents »
//                      + pastille de compte) ;
//   4. « Détails »   → le panneau complet (seul à ouvrir un panneau), à droite.
//
// ⚠️ COMMENT UN BOUTON GRISÉ DIT POURQUOI (forme validée Dimitri) : le MOT
// sous l'icône ne change jamais — la raison vit dans l'infobulle `title` ET
// dans l'aria-label (« Contrôler — déjà contrôlé »). Écrire la raison à la
// place du mot cassait la règle « les mots sont obligatoires », et une
// deuxième ligne ne tient pas lisiblement sur un quart de 280 px.
//
// ⚠️ EMPILEMENT : le menu vit dans une ligne à `opacity` (contexte
// d'empilement), surélevée à zIndex 60 quand il est ouvert. Les sous-panneaux
// Contrôler et Incidents restent DANS ce contexte (déroulés sous la barre) ;
// la fenêtre Code est une MODALE PLEIN ÉCRAN `position:fixed` — l'opacity ne
// crée pas de bloc conteneur pour fixed, seul un transform le ferait.
//
// ⚠️ INTERVENTIONS (bloc sans run) : la barre ne porte que « Détails ». Les
// trois autres gestes parlent d'un MÉNAGE (codes datés du guichet, contrôle,
// incident d'un run) — les griser sur une intervention afficherait des
// raisons fausses (« une fois terminé » sur un bloc qui ne se termine pas).

import { useState, useRef, useEffect, useCallback } from 'react'
import PortailFeuille from '../../components/PortailFeuille'
import useVoileEcran from '../../lib/useVoileEcran'
import { DS } from '../../lib/ui'
import useEchapPourFermer from '../../lib/useEchapPourFermer'
import useEtroit from '../../lib/useEtroit'
import { dansFenetreControle, raisonFenetrePassee } from '../../lib/controle-fenetre'
import { normalizeStatus, STATUS as STATUS_INCIDENTS } from '../../lib/incidents-api'
import { firstNameFromEmail, initialsFromEmail } from './RunCard'
// L'ordre des codes (boîte à clés EN TÊTE) et « il y a N h » sont ceux du
// panneau — une même information ne se formate que d'une seule façon.
import { lignesCodes, ilYATexte, controleAttend } from './PanneauMenage'
// La double-forme de la checklist vit dans ControleView (source unique).
import { lignesChecklist } from './ControleView'
import { Key, Eye, EyeOff, AlertTriangle, PanelRight, X, CircleCheck, ClipboardCheck, ChevronLeft, RotateCcw } from 'lucide-react'

// ── Palette de la barre (recalage Dimitri 2026-08-19 : chaque bouton porte
// SA couleur en fond ; « Détails » se détache franchement — c'est la porte
// principale ; les grisés perdent TOUTE leur couleur). ──────────────────────
const B = {
  fond: '#f6f8fa', bord: '#c3d5e4', mot: '#3d5166', navy: '#2e3d4d',
  gris: '#98a2ad',
  terraBord: '#e0b5a5', terraFond: '#fdf4f0', terra: '#8f2f1d',
  vert: '#2c7a5c', vertSombre: '#1f5a43', vertFond: '#eaf4ef', vertBord: '#c9e0d4',
}
// Les teintes PAR BOUTON (actif) et LA teinte grisée unique.
const TEINTES_BOUTON = {
  code: { fond: '#e8f2fa', bord: '#c3d5e4', texte: '#3d5166' },
  controler: { fond: '#f0f7f3', bord: '#c9e0d4', texte: '#1f5a43' },
  signaler: { fond: '#fdf4f0', bord: '#e0b5a5', texte: '#8f2f1d' },
  // « Checklist » (orchestrateur = agent assigné) : bordure 2 px ciel — le
  // seul bouton à bordure épaisse, c'est SON ménage.
  checklist: { fond: '#e8f2fa', bord: '#5c8ab8', bordEpais: true, texte: '#3d5166' },
  details: { fond: 'linear-gradient(115deg, #3d5166 0%, #5c8ab8 100%)', bord: null, texte: '#fff' },
}
const TEINTE_GRISE = { fond: '#f2f4f6', bord: '#e3e8ee', texte: '#98a2ad' }

// ── Un bouton de la barre : icône 19 px + MOT 9.5 px gras dessous, 56 px.
// Recalage 2026-08-19 : chaque bouton porte SA couleur (teinte par nom) ; un
// bouton GRISÉ perd TOUTE sa couleur (fond #f2f4f6, opacité .6, icône barrée
// quand elle existe — iconeGrise) — le contraste avec l'actif est immédiat.
// La barre PARLE avant qu'on la touche : `compte` (pastille terracotta
// bordée de blanc) et `point` (8 px, l'état du contrôle) en haut à droite.
function BoutonBarre({ testId, icone: Icone, iconeGrise: IconeGrise = null,
  mot, actif, raison = null, teinte = 'code', onClick, compte = 0,
  point = null, progression = null, ouvert = false, etroit = false }) {
  // SPEC §7 (étroit) : « Contrôler » passe en VERT (#f0f7f3 / #124a33) —
  // l'ordinateur garde ses teintes historiques.
  const teintes = actif
    ? (etroit && teinte === 'controler'
        ? { fond: '#f0f7f3', bord: '#c9e0d4', texte: '#124a33' }
        : TEINTES_BOUTON[teinte])
    : TEINTE_GRISE
  const IconeRendue = (!actif && IconeGrise) ? IconeGrise : Icone
  return (
    <button type="button" data-testid={testId} disabled={!actif}
      title={raison || undefined}
      aria-label={raison ? `${mot} — ${raison}` : mot}
      aria-expanded={teinte === 'details' ? undefined : ouvert || undefined}
      style={{ position: 'relative', height: etroit ? 58 : 56, minWidth: 0,
        border: teintes.bord ? `${teintes.bordEpais ? 2 : 1}px solid ${teintes.bord}` : 'none',
        borderRadius: 10, background: teintes.fond, opacity: actif ? 1 : 0.6,
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', gap: 3, padding: 0,
        cursor: actif ? 'pointer' : 'default', fontFamily: DS.fontBody }}
      onClick={actif ? (e) => { e.stopPropagation(); onClick() } : undefined}>
      <IconeRendue size={19} strokeWidth={2} color={teintes.texte} />
      <span style={{ fontSize: 9.5, fontWeight: 700, color: teintes.texte,
        maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' }}>{mot}</span>
      {compte > 0 && (
        <span data-testid={`${testId}-compte`}
          style={{ position: 'absolute', top: 3, right: 4, minWidth: 17, height: 17,
            padding: '0 5px', borderRadius: 9, background: '#b8412b', color: '#fff',
            border: '1.5px solid #fff', fontSize: 9.5, fontWeight: 700,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            boxSizing: 'border-box' }}>
          {compte}
        </span>
      )}
      {point && (
        <span data-testid={`${testId}-point`} aria-hidden="true"
          style={{ position: 'absolute', top: 4, right: 5, width: 8, height: 8,
            borderRadius: '50%', border: '1.5px solid #fff', boxSizing: 'content-box',
            background: point === 'attente' ? '#b8412b' : '#7fc4a3' }} />
      )}
      {progression && (
        <span data-testid={`${testId}-progression`}
          style={{ position: 'absolute', top: 3, right: 4, height: 17,
            padding: '0 5px', borderRadius: 9, background: '#5c8ab8', color: '#fff',
            border: '1.5px solid #fff', fontSize: 9.5, fontWeight: 700,
            fontVariantNumeric: 'tabular-nums', display: 'inline-flex',
            alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box' }}>
          {progression}
        </span>
      )}
    </button>
  )
}

// ── « CODE » — la fenêtre plein écran : TOUS les codes renseignés, EN GROS,
// chacun avec « Copier » (retour « Copié » 2 s, PAR code). RIEN d'autre. ─────
function FenetreCodes({ bloc, codes, onFermer }) {
  useVoileEcran()
  // TÉLÉPHONE (cadrage 2026-08-22) : la fenêtre MONTE DU BAS.
  const etroitCodes = useEtroit()
  useEchapPourFermer(onFermer)
  const [copie, setCopie] = useState({}) // { [cle]: 'ok' | 'echec' }
  const timers = useRef({})
  useEffect(() => () => {
    for (const t of Object.values(timers.current)) clearTimeout(t)
  }, [])
  const copier = useCallback((cle, code) => {
    const poser = (etat) => {
      setCopie((c) => ({ ...c, [cle]: etat }))
      clearTimeout(timers.current[cle])
      timers.current[cle] = setTimeout(() => {
        setCopie((c) => { const n = { ...c }; delete n[cle]; return n })
      }, 2000)
    }
    try {
      const p = navigator.clipboard?.writeText?.(code)
      if (p && typeof p.then === 'function') p.then(() => poser('ok'), () => poser('echec'))
      else if (p !== undefined) poser('ok')
      else poser('echec')
    } catch (e) {
      console.error('[barre-actions] copie code', e?.message || e)
      poser('echec')
    }
  }, [])
  return (
    <PortailFeuille actif={etroitCodes}>
    <div data-testid={`frise-code-fenetre-${bloc.id}`} role="dialog" aria-modal="true"
      aria-label={`Codes d'accès — ${bloc.nom}`}
      onPointerDown={(e) => e.stopPropagation()}
      onClick={(e) => { if (e.target === e.currentTarget) onFermer() }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(46,61,77,.42)',
        // TÉLÉPHONE (correction 22/08) : la feuille du menu vit à z999 — à
        // z60 la fenêtre des codes s'ouvrait DERRIÈRE elle (« plus rien à
        // l'écran »). Le second niveau monte AU-DESSUS, ancré à l'écran.
        zIndex: etroitCodes ? 1200 : 60,
        display: 'flex', alignItems: etroitCodes ? 'flex-end' : 'center',
        justifyContent: 'center', padding: etroitCodes ? 0 : 16 }}>
      <div style={{ background: '#fff', borderRadius: etroitCodes ? '18px 18px 0 0' : 12,
        padding: 14, width: '100%',
        maxWidth: etroitCodes ? 'none' : 340, maxHeight: '86vh', overflowY: 'auto', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: 8, fontFamily: DS.fontBody,
        boxShadow: '0 12px 34px rgba(33,83,135,.28)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* TÉLÉPHONE : une FLÈCHE DE RETOUR vers la feuille d'avant (le menu
              reste ouvert dessous) — pas une croix qui laisse croire qu'on
              ferme tout. Ordinateur : la croix historique. */}
          {etroitCodes && (
            <button type="button" data-testid={`frise-code-retour-${bloc.id}`}
              onClick={onFermer} aria-label="Revenir au menu" className="fbz-press"
              style={{ width: 32, height: 32, borderRadius: 9, border: 'none',
                background: '#f4f6f8', color: '#3d5166', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
                flexShrink: 0 }}>
              <ChevronLeft size={17} strokeWidth={2.2} />
            </button>
          )}
          <span style={{ flex: 1, fontSize: etroitCodes ? 14.5 : 12.5, fontWeight: 700,
            color: etroitCodes ? '#233240' : B.navy,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {bloc.nom}
          </span>
          {!etroitCodes && (
          <button type="button" data-testid={`frise-code-fermer-${bloc.id}`}
            onClick={onFermer} aria-label="Fermer"
            style={{ width: 32, height: 32, border: 'none', background: 'none',
              padding: 0, cursor: 'pointer', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', color: B.gris }}>
            <X size={16} strokeWidth={2.2} />
          </button>
          )}
        </div>
        {codes.map(({ cle, label, code }) => (
          <div key={cle} data-testid={`frise-code-${cle}`}
            style={{ display: 'flex', alignItems: 'center', gap: 11,
              background: `linear-gradient(115deg, ${B.navy} 0%, #87b8e6 100%)`,
              borderRadius: 11, padding: '10px 12px' }}>
            <span aria-hidden="true" style={{ width: 36, height: 36, borderRadius: 10,
              background: 'rgba(255,255,255,.16)', display: 'inline-flex',
              alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Key size={16} strokeWidth={2.1} color="#fff" />
            </span>
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 9.5, fontWeight: 700,
                color: 'rgba(255,255,255,.72)', textTransform: 'uppercase',
                letterSpacing: '.09em' }}>{label}</span>
              <span data-testid={`frise-code-valeur-${cle}`}
                style={{ display: 'block', fontSize: 22, fontWeight: 700, color: '#fff',
                  letterSpacing: '.15em', fontVariantNumeric: 'tabular-nums',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {code}
              </span>
            </span>
            <button type="button" data-testid={`frise-code-copier-${cle}`}
              onClick={() => copier(cle, code)} className="fbz-press"
              aria-label={`Copier le code ${label.toLowerCase()}`}
              style={{ flexShrink: 0, minHeight: 32, padding: '5px 12px',
                border: '1px solid rgba(255,255,255,.3)', background: 'rgba(255,255,255,.12)',
                borderRadius: 8, fontSize: 10.5, fontWeight: 700, color: '#fff',
                cursor: 'pointer', fontFamily: DS.fontBody }}>
              {copie[cle] === 'ok' ? 'Copié' : copie[cle] === 'echec' ? 'Copie impossible' : 'Copier'}
            </button>
          </div>
        ))}
      </div>
    </div>
    </PortailFeuille>
  )
}

// ── « CONTRÔLER » — le CHOIX, dans le menu (pas une navigation sèche). ──────
function ChoixControle({ bloc, ctx, onControler }) {
  const run = bloc.run
  const defaut = ctx?.controleurDefautParLogement?.[bloc.logementId] || null
  const designe = run.controleur_designe_email || null
  const options = ctx?.assignOptions || []
  return (
    <div data-testid={`frise-controle-choix-${bloc.id}`}
      style={{ borderTop: `1px solid ${B.bord}`, background: '#fff',
        padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 7 }}>
      {onControler && (
        <button type="button" data-testid={`frise-controle-maintenant-${bloc.id}`}
          onClick={(e) => { e.stopPropagation(); onControler(run) }}
          className="fbz-press"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, minHeight: 40, width: '100%', border: `1px solid ${B.vertBord}`,
            background: B.vertFond, color: B.vertSombre, borderRadius: 9,
            fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
          <Eye size={14} strokeWidth={2.1} /> Je contrôle maintenant
        </button>
      )}
      {ctx?.onDesignerControleur && options.length > 0 && (
        <>
          <span style={{ fontSize: 9.5, fontWeight: 700, color: B.mot,
            textTransform: 'uppercase', letterSpacing: '.07em' }}>
            Assigner le contrôle à…
          </span>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {options.map((a) => {
              const actif = a.value === designe
              const estDefaut = a.value === defaut
              return (
                <button key={a.value} type="button"
                  data-testid={`frise-controleur-${bloc.id}-${a.value}`}
                  aria-pressed={actif}
                  onClick={(e) => { e.stopPropagation(); ctx.onDesignerControleur(run, a.value) }}
                  style={{ display: 'inline-flex', flexDirection: 'column',
                    alignItems: 'flex-start', gap: 1,
                    border: `1px solid ${actif ? B.vertSombre : B.vertBord}`,
                    background: actif ? B.vertFond : '#fff',
                    color: actif ? B.vertSombre : B.mot,
                    borderRadius: 7, padding: '5px 9px', fontSize: 11, fontWeight: 700,
                    cursor: 'pointer', fontFamily: DS.fontBody, whiteSpace: 'nowrap' }}>
                  <span>{a.isSelf ? a.label : `${initialsFromEmail(a.value)} · ${a.label}`}</span>
                  {estDefaut && (
                    <span data-testid={`frise-controleur-defaut-${bloc.id}`}
                      style={{ fontSize: 9.5, fontWeight: 400, color: B.gris }}>
                      par défaut · contrôleur de ce logement
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        </>
      )}
    </div>
  )
}

// ── DÉJÀ CONTRÔLÉ — LE CHOIX (option B, GO Dimitri 22/08) : on n'écrase
// JAMAIS un verdict déposé. « Voir le contrôle » = la trace existante (points,
// photos, qui, quand — le détail de la pastille œil). « Refaire un contrôle »
// = une NOUVELLE ligne en base (le serveur n'a qu'un INSERT ; l'index unique
// ne verrouille qu'un contrôle OUVERT) — la plus récente fait foi, l'ancienne
// reste dans l'historique, ET LA PHRASE LE DIT AVANT DE PARTIR. La fenêtre
// contrat + 12 h s'applique au re-contrôle : au-delà, « Refaire » se grise
// avec sa raison écrite, « Voir » reste. Téléphone ET ordinateur.
const MOIS_MIN_CTRL = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet',
  'août', 'septembre', 'octobre', 'novembre', 'décembre']
function dateCourteControle(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''))
  return m ? `${Number(m[3])} ${MOIS_MIN_CTRL[Number(m[2]) - 1]}` : null
}

function ChoixReControle({ bloc, ctx, controle, onControler, heureContrat }) {
  const run = bloc.run
  const enFenetre = dansFenetreControle(run.scheduled_date, heureContrat)
  const dateCtrl = dateCourteControle(controle?.cree_le)
  const peutVoir = !!(ctx?.onOuvrirControle && controle)
  const peutRefaire = !!onControler
  return (
    <div data-testid={`frise-recontrole-choix-${bloc.id}`}
      style={{ borderTop: `1px solid ${B.bord}`, background: '#fff',
        padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 7 }}>
      {peutVoir && (
        <button type="button" data-testid={`frise-voir-controle-${bloc.id}`}
          onClick={(e) => { e.stopPropagation(); ctx.onOuvrirControle(controle) }}
          className="fbz-press"
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            gap: 6, minHeight: 40, width: '100%', border: '1px solid #c3d5e4',
            background: '#e8f2fa', color: '#2e3d4d', borderRadius: 9,
            fontSize: 11.5, fontWeight: 700, cursor: 'pointer', fontFamily: DS.fontBody }}>
          <Eye size={14} strokeWidth={2.1} /> Voir le contrôle{dateCtrl ? ` du ${dateCtrl}` : ''}
        </button>
      )}
      {peutRefaire && (
        <>
          {/* LA PHRASE AVANT DE PARTIR — le geste dit ce qu'il fait. */}
          <span data-testid={`frise-recontrole-phrase-${bloc.id}`}
            style={{ fontSize: 10.5, color: '#5c6773', lineHeight: 1.45 }}>
            {enFenetre
              ? `Le contrôle${dateCtrl ? ` du ${dateCtrl}` : ''} restera dans l'historique.`
              : raisonFenetrePassee(run.scheduled_date, heureContrat)}
          </span>
          <button type="button" data-testid={`frise-refaire-controle-${bloc.id}`}
            disabled={!enFenetre}
            onClick={enFenetre ? (e) => { e.stopPropagation(); onControler(run) } : undefined}
            className="fbz-press"
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              gap: 6, minHeight: 40, width: '100%',
              border: `1px solid ${enFenetre ? B.vertBord : '#e3e8ee'}`,
              background: enFenetre ? B.vertFond : '#f2f4f6',
              color: enFenetre ? B.vertSombre : B.gris, borderRadius: 9,
              fontSize: 11.5, fontWeight: 700, opacity: enFenetre ? 1 : 0.7,
              cursor: enFenetre ? 'pointer' : 'default', fontFamily: DS.fontBody }}>
            <RotateCcw size={14} strokeWidth={2.1} /> Refaire un contrôle
          </button>
        </>
      )}
    </div>
  )
}

// ── « SIGNALER » côté LECTURE — la liste des incidents du ménage. Titres +
// « signalé par X · il y a N h » + état ouvert/réglé. RIEN de cliquable. ─────
function ListeIncidents({ bloc, incidents }) {
  return (
    <div data-testid={`frise-incidents-liste-${bloc.id}`}
      style={{ borderTop: `1px solid ${B.bord}`, background: '#fff',
        padding: '9px 10px', display: 'flex', flexDirection: 'column', gap: 6 }}>
      {incidents.map((inc) => {
        const ouvert = normalizeStatus(inc.status) === STATUS_INCIDENTS.EN_COURS
        const prenom = firstNameFromEmail(inc.reported_by) || null
        const signe = [prenom ? `signalé par ${prenom}` : 'signalé', ilYATexte(inc.created_at)]
          .filter(Boolean).join(' · ')
        return (
          <div key={inc.id} data-testid={`frise-incident-${inc.id}`}
            style={{ display: 'flex', alignItems: 'center', gap: 8,
              background: ouvert ? '#fdf4f0' : '#fff',
              border: `1px solid ${ouvert ? B.terraBord : '#e3e8ee'}`,
              borderRadius: 8, padding: '7px 10px' }}>
            {ouvert
              ? <AlertTriangle size={14} strokeWidth={2.2} color={B.terra} style={{ flexShrink: 0 }} />
              : <CircleCheck size={14} strokeWidth={2.2} color={B.vert} style={{ flexShrink: 0 }} />}
            <span style={{ flex: 1, minWidth: 0 }}>
              <span style={{ display: 'block', fontSize: 11.5, fontWeight: 700,
                color: ouvert ? B.terra : B.mot, overflow: 'hidden',
                textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {inc.label || 'Incident'}
              </span>
              <span style={{ display: 'block', fontSize: 10, marginTop: 1, color: B.gris }}>
                {signe}
              </span>
            </span>
            <span data-testid={`frise-incident-etat-${inc.id}`}
              style={{ flexShrink: 0, fontSize: 9.5, fontWeight: 700,
                color: ouvert ? B.terra : B.vertSombre }}>
              {ouvert ? 'ouvert' : 'réglé'}
            </span>
          </div>
        )
      })}
    </div>
  )
}

// ═══ LA BARRE ═══════════════════════════════════════════════════════════════

export default function BarreActionsBloc({ bloc, ctx = null, controle = null,
  onControler = null, onOuvrirDetail = null, onTailleChange = null }) {
  // UN SEUL sous-panneau à la fois : 'code' | 'controle' | 'incidents' | null.
  const [sousPanneau, setSousPanneau] = useState(null)
  // TÉLÉPHONE : hook EN TÊTE (avant tout retour anticipé — règle des hooks).
  const etroit = useEtroit()
  const basculer = useCallback((nom) => {
    setSousPanneau((s) => (s === nom ? null : nom))
    // Le menu se re-mesure (bascule haut/bas) quand la barre change de taille.
    if (onTailleChange) onTailleChange()
  }, [onTailleChange])

  const run = bloc.run || null

  // INTERVENTION — « Détails » seul (voir l'en-tête du fichier).
  if (!run) {
    if (!onOuvrirDetail) return null
    return (
      <div style={{ borderTop: `1px solid ${B.bord}`, background: B.fond, padding: 7 }}>
        <BoutonBarre etroit={etroit} testId={`frise-detail-ouvrir-${bloc.id}`} icone={PanelRight}
          mot="Détails" actif teinte="details"
          onClick={() => onOuvrirDetail(bloc.id)} />
      </div>
    )
  }

  // ── 1. « CODE » — grisé si aucun code renseigné (guichet pas encore là
  // compris : la donnée se lit AU RENDU, jamais au chargement). ──────────────
  const info = ctx?.infoFor ? ctx.infoFor(bloc.logementId, run.scheduled_date) : null
  const codes = lignesCodes(info?.codes)
  const codeActif = codes.length > 0

  // ── 2. « CONTRÔLER » — l'état grisé reprend MOT POUR MOT les mentions de
  // l'ancien menu (« déjà contrôlé », « pas encore contrôlé ») ; le non-fini
  // dit « une fois terminé ». Même garde 72 h que les mentions actuelles. ────
  const fini = run.status === 'completed'
  const peutAgir = !!(onControler || ctx?.onDesignerControleur)
  // LA FENÊTRE RÉELLE (recalage 22/08 — règle du 21/08, contrat + 12 h) : le
  // miroir 72 h proposait des contrôles que le serveur refusait en 410.
  // checkinTimeByLog est une FONCTION (useCallback de MenageSuiviView) — l'accès
  // par index renvoyait toujours undefined et la fenêtre retombait sur 16 h.
  const heureContrat = ctx?.checkinTimeByLog?.(bloc.logementId) || null
  let raisonControle = null
  // DÉJÀ CONTRÔLÉ ≠ grisé (option B, 22/08) : le bouton ouvre le CHOIX —
  // « Voir le contrôle » (toujours) / « Refaire » (dans la fenêtre).
  if (controle) raisonControle = null
  else if (!fini) raisonControle = 'une fois terminé'
  else if (!dansFenetreControle(run.scheduled_date, heureContrat)) {
    raisonControle = raisonFenetrePassee(run.scheduled_date, heureContrat)
  }
  else if (!peutAgir) raisonControle = 'pas encore contrôlé'
  const controleActif = !raisonControle && (!controle || !!ctx?.onOuvrirControle || !!onControler)

  // ── 3. « SIGNALER » — DEUX NATURES selon l'utilisateur. ───────────────────
  const estAgent = !!(ctx?.self && bloc.agent && ctx.self === bloc.agent)
  // L'avancement de MA checklist — « 4/18 » ; rien sans lignes.
  const lignesMoi = estAgent ? lignesChecklist(run.items_checked) : []
  const progressionChecklist = lignesMoi.length > 0
    ? `${lignesMoi.filter((l) => l.coche).length}/${lignesMoi.length}` : null
  // Le filtre est CELUI du panneau : run_id égal OU même logement.
  const incidentsMenage = Array.isArray(ctx?.incidentsPanneau)
    ? ctx.incidentsPanneau.filter((i) => i && (i.run_id === run.id || i.logement_id === bloc.logementId))
    : []
  const signalerAutorise = estAgent && !!ctx?.incidentAutorise && ctx.incidentAutorise(run)
  const signalerActif = estAgent ? signalerAutorise : incidentsMenage.length > 0
  const raisonSignaler = signalerActif ? null
    : estAgent ? 'Signalement disponible le jour du ménage'
      : 'aucun incident sur ce ménage'

  const nbBoutons = onOuvrirDetail ? 4 : 3
  // TÉLÉPHONE (cadrage 2026-08-22) : quatre boutons en GRILLE DE DEUX, et
  // CE QUI VIVAIT AU SURVOL S'ÉCRIT — un grisé muet n'explique rien au doigt.
  const raisonsGrisees = [
    !codeActif && ['Code', 'aucun code renseigné'],
    !estAgent && !controleActif && raisonControle && ['Contrôler', raisonControle],
    !signalerActif && raisonSignaler && [estAgent ? 'Signaler' : 'Incidents', raisonSignaler],
  ].filter(Boolean)

  return (
    <>
      <div data-testid={`frise-barre-actions-${bloc.id}`}
        style={{ borderTop: `1px solid ${B.bord}`, background: B.fond, padding: 7,
          display: 'grid', gridTemplateColumns: etroit ? 'repeat(2, 1fr)' : `repeat(${nbBoutons}, 1fr)`, gap: 7 }}>
        <BoutonBarre etroit={etroit} testId={`frise-menu-code-${bloc.id}`} icone={Key} mot="Code"
          teinte="code"
          actif={codeActif} raison={codeActif ? null : 'aucun code renseigné'}
          ouvert={sousPanneau === 'code'} onClick={() => basculer('code')} />
        {/* EXCLUSIF (Dimitri 2026-08-19) : l'orchestrateur ASSIGNÉ ne
            contrôle pas son propre travail — « Contrôler » devient
            « Checklist » (le geste d'un agent), avec l'AVANCEMENT en pastille
            ciel : un progrès, pas une alerte. Jamais les deux boutons. */}
        {estAgent ? (
          <BoutonBarre etroit={etroit} testId={`frise-menu-checklist-${bloc.id}`} icone={ClipboardCheck}
            mot="Checklist" teinte="checklist" actif
            progression={progressionChecklist}
            onClick={() => ctx.onOpenChecklist && ctx.onOpenChecklist(run)} />
        ) : (
          <BoutonBarre etroit={etroit} testId={`frise-menu-controler-${bloc.id}`} icone={Eye}
            iconeGrise={EyeOff} mot="Contrôler" teinte="controler"
            point={controle ? (controleAttend(controle) ? 'attente' : 'fait') : null}
            actif={controleActif} raison={raisonControle}
            ouvert={sousPanneau === 'controle'} onClick={() => basculer('controle')} />
        )}
        <BoutonBarre etroit={etroit} testId={`frise-menu-signaler-${bloc.id}`} icone={AlertTriangle}
          mot={estAgent ? 'Signaler' : 'Incidents'}
          actif={signalerActif} raison={raisonSignaler}
          teinte="signaler"
          compte={estAgent ? 0 : incidentsMenage.length}
          ouvert={sousPanneau === 'incidents'}
          onClick={estAgent
            // L'agent assigné → le FORMULAIRE d'incident (geste existant).
            ? () => ctx.onSignalIncident && ctx.onSignalIncident(
              run, ctx.statutEffectif ? ctx.statutEffectif(run) : null)
            // Tout autre → la LISTE lecture des incidents du ménage.
            : () => basculer('incidents')} />
        {onOuvrirDetail && (
          <BoutonBarre etroit={etroit} testId={`frise-detail-ouvrir-${bloc.id}`} icone={PanelRight}
            mot="Détails" actif teinte="details"
            onClick={() => onOuvrirDetail(bloc.id)} />
        )}
      </div>
      {/* AU DOIGT, LA RAISON S'ÉCRIT (cadrage 2026-08-22) : l'infobulle d'un
          bouton grisé n'existe pas sur téléphone — chaque raison vit ici, en
          toutes lettres, sous la grille. */}
      {etroit && raisonsGrisees.length > 0 && (
        <div data-testid={`frise-barre-raisons-${bloc.id}`}
          style={{ margin: '0 9px 8px', padding: '7px 10px', background: '#f8fafc',
            borderRadius: 9 }}>
          {raisonsGrisees.map(([mot, raison]) => (
            <div key={mot} style={{ fontSize: 10.5, color: '#98a2ad', lineHeight: 1.5 }}>
              {mot} — {raison}
            </div>
          ))}
        </div>
      )}
      {sousPanneau === 'controle' && controleActif && !controle && (
        <ChoixControle bloc={bloc} ctx={ctx} onControler={onControler} />
      )}
      {sousPanneau === 'controle' && controleActif && controle && (
        <ChoixReControle bloc={bloc} ctx={ctx} controle={controle}
          onControler={onControler} heureContrat={heureContrat} />
      )}
      {sousPanneau === 'incidents' && !estAgent && incidentsMenage.length > 0 && (
        <ListeIncidents bloc={bloc} incidents={incidentsMenage} />
      )}
      {sousPanneau === 'code' && codeActif && (
        <FenetreCodes bloc={bloc} codes={codes} onFermer={() => basculer('code')} />
      )}
    </>
  )
}
